import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import {
  Brain, ShoppingCart, Clock, FileText, Users, Shield, TrendingUp,
  Smartphone, BarChart3, Wrench, CalendarDays, MessageCircle,
  ArrowRight, Check, ChevronRight, BookOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';
import PublicChatWidget from '../components/PublicChatWidget';
import SEO from '../components/SEO';

const HOME_JSON_LD = [
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Operio',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, iOS, Android (PWA)',
    url: 'https://operio.nu',
    description: 'AI-plattform för bygg-, mark- och entreprenadföretag i Sverige. Kalkyl, tidrapportering, fakturering och bokföring i ett system.',
    offers: { '@type': 'Offer', price: '1990', priceCurrency: 'SEK', priceValidUntil: '2026-12-31' },
    aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.8', reviewCount: '47' },
    featureList: 'AI kalkyl, GPS-tidrapportering, fakturering, BankID-signering, AI-bokföring, kundportal',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: 'Vad är Operio?', acceptedAnswer: { '@type': 'Answer', text: 'Operio är en AI-driven SaaS-plattform för svenska bygg-, mark- och entreprenadföretag. Vi kombinerar kalkyl, tidrapportering, fakturering, bokföring och BankID-signering i ett enda system.' } },
      { '@type': 'Question', name: 'Hur snabbt kan jag komma igång?', acceptedAnswer: { '@type': 'Answer', text: '5 minuter. Skapa konto, importera dina priser och du kan räkna din första offert med AI. 14 dagar gratis utan kortuppgifter.' } },
      { '@type': 'Question', name: 'Funkar Operio offline?', acceptedAnswer: { '@type': 'Answer', text: 'Ja, Worker-appen (PWA) fungerar offline för tidrapportering och synkar när nätet kommer tillbaka.' } },
      { '@type': 'Question', name: 'Är Operio dyrt?', acceptedAnswer: { '@type': 'Answer', text: 'Från 1 990 kr/månad exkl. moms. De flesta byggare betalar tillbaka kostnaden inom första veckan tack vare snabbare offerter och korrekt tidrapportering.' } },
    ],
  },
];

const LandingPage = () => {
  const { user } = useAuth();

  if (user) {
    if (user.role === 'superadmin') return <Navigate to="/superadmin" />;
    if (user.role === 'worker') return <Navigate to="/worker" />;
    return <Navigate to="/dashboard" />;
  }

  const features = [
    { icon: Brain, title: 'AI Materialberäkning', desc: 'Beskriv ditt jobb — AI beräknar exakta material med produktnamn, priser och rekommenderade leverantörer.' },
    { icon: ShoppingCart, title: 'Inköpslistor & Leverantörer', desc: 'Generera inköpslistor, jämför priser mellan leverantörer och skanna leverantörsfakturor med OCR.' },
    { icon: TrendingUp, title: 'Upphandlingsanalys', desc: 'Ladda upp anbudsdokument — AI extraherar krav, deadlines och kvalificeringsbedömning automatiskt.' },
    { icon: FileText, title: 'Fakturering & ROT', desc: 'Skapa fakturor direkt från projektdata. Stöd för ROT-avdrag med automatisk beräkning.' },
    { icon: Clock, title: 'Stämpling, Timer & Löner', desc: 'Personal stämplar in/ut via mobilen med pinkod och GPS. Faktisk arbetstid, OB och lönespec beräknas automatiskt.' },
    { icon: BookOpen, title: 'AI Bokföring', desc: 'Automatisk bokföring med BAS-kontoplan. AI bokför fakturor, löner och inköp. Resultat- och balansräkning per kvartal.' },
    { icon: BarChart3, title: 'Dagbok & Rapporter', desc: 'AI-genererade dagböcker baserade på dagens arbete. PDF-export av allt med ett klick.' },
    { icon: CalendarDays, title: 'Kalender & Påminnelser', desc: 'Håll koll på deadlines, besiktningar och leveranser med automatiska påminnelser.' },
    { icon: Users, title: 'Personal & Kundportal', desc: 'Hantera anställda med behörigheter. Ge kunder insyn i sina projekt via en dedikerad portal.' },
    { icon: Wrench, title: 'Maskiner & ÄTA', desc: 'Logga maskintimmar och hantera tilläggsarbeten med kostnadsberäkning och geofencing.' },
    { icon: Shield, title: 'Geofencing & GPS', desc: 'Sätt arbetsområden per projekt. Systemet varnar om personal stämplar in utanför zonen.' },
    { icon: Smartphone, title: 'Mobilapp (PWA)', desc: 'Installera Operio direkt på telefonen via webbläsaren. Fungerar i fält utan app store.' },
  ];

  const plans = [
    { name: 'Start', price: '1 990', period: '/mån', desc: 'Grundpaketet för entreprenörer', perUser: '1 användare ingår · +199 kr/extra', features: ['Jobbkalkyl & material', 'Offert & PDF + e-post', 'Projekthantering', 'Kalender & påminnelser'] },
    { name: 'Pro', price: '2 990', period: '/mån', desc: 'Mest populär — allt du behöver', perUser: '1 användare ingår · +149 kr/extra', features: ['Allt i Start', 'Fakturering & ROT', 'Tidrapportering', 'Maskintimmar', 'ÄTA-hantering'], highlighted: true },
    { name: 'Business', price: '4 490', period: '/mån', desc: 'Full kontroll & support', perUser: '1 användare ingår · +99 kr/extra', features: ['Allt i Pro', 'Lönehantering & lönespec', 'AI Bokföring', 'Kundportal', 'Prioriterad support'] },
  ];

  const steps = [
    { num: '01', title: 'Skapa konto', desc: 'Registrera ditt företag och verifiera dina uppgifter. Klart på under 2 minuter.' },
    { num: '02', title: 'Lägg in projekt', desc: 'Skapa projekt, koppla anställda och maskiner. Låt AI hjälpa med materialberäkning och kalkyl.' },
    { num: '03', title: 'Kör igång', desc: 'Personal stämplar i fält, du får realtidsöversikt. Fakturor, löner och bokföring sköts automatiskt.' },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F4EF' }} data-testid="landing-page">
      <SEO
        title="Operio – AI för bygg & entreprenad | Kalkyl, tidrapportering & administration"
        description="Operio är AI-plattformen som digitaliserar svenska bygg-, mark- och entreprenadföretag. Kalkyl, GPS-tidrapportering, fakturering, BankID-signering och AI-bokföring i ett system. 14 dagar gratis."
        canonical="/"
        keywords="bygg kalkyl program, AI byggsystem, bygg administration, tidrapportering bygg, entreprenad system, bygg fakturering, bygg saas sverige"
        jsonLd={HOME_JSON_LD}
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#F7F4EF]/90 backdrop-blur-md border-b border-[#E8E2D9]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" aria-label="Operio startsida">
            <img src="/operio-logo-2.png" alt="Operio logotyp" className="h-7 sm:h-8 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm text-[#6B6560]" aria-label="Huvudnavigation">
            <Link to="/bygg-kalkyl-program" className="hover:text-[#1A1A1A] transition-colors">Kalkyl</Link>
            <Link to="/tidrapportering-bygg" className="hover:text-[#1A1A1A] transition-colors">Tidrapportering</Link>
            <Link to="/priser" className="hover:text-[#1A1A1A] transition-colors">Priser</Link>
            <Link to="/blogg" className="hover:text-[#1A1A1A] transition-colors">Blogg</Link>
            <Link to="/about" className="hover:text-[#1A1A1A] transition-colors">Om oss</Link>
            <Link to="/kontakt" className="hover:text-[#1A1A1A] transition-colors" data-testid="landing-kontakt-link">Kontakt</Link>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <Link to="/login" className="text-xs sm:text-sm font-medium text-[#6B6560] hover:text-[#1A1A1A] transition-colors px-2" data-testid="landing-login-link">
              Logga in
            </Link>
            <Link to="/register" data-testid="landing-cta-btn">
              <Button className="bg-[#E8613A] hover:bg-[#D4532F] text-white text-xs sm:text-sm h-8 sm:h-9 px-3 sm:px-5 rounded-full whitespace-nowrap">
                Kom igång<span className="hidden sm:inline"> gratis</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-24 sm:pt-28 pb-12 sm:pb-20 px-5 sm:px-6" data-testid="hero-section">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="text-center lg:text-left">
            <h1 className="text-[2.2rem] sm:text-5xl lg:text-[3.5rem] font-bold tracking-tight text-[#1A1A1A] leading-[1.05]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              AI för bygg- och entreprenad­företag
            </h1>
            <p className="text-sm sm:text-base text-[#6B6560] mt-5 sm:mt-6 max-w-lg mx-auto lg:mx-0 leading-relaxed">
              Operio är allt-i-ett SaaS-plattformen som digitaliserar svenska bygg-, mark- och VVS-företag —
              <strong className="text-[#1A1A1A]"> kalkyl, tidrapportering, fakturering, AI-bokföring och BankID-signering</strong> i ett system. 14 dagar gratis.
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center lg:justify-start gap-3 sm:gap-4 mt-7 sm:mt-8 max-w-sm sm:max-w-none mx-auto lg:mx-0">
              <Link to="/register">
                <Button className="w-full sm:w-auto bg-[#E8613A] hover:bg-[#D4532F] text-white h-12 px-7 text-sm sm:text-base rounded-full shadow-lg shadow-[#E8613A]/20" data-testid="hero-cta-btn">
                  Testa gratis i 14 dagar <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/about">
                <Button variant="outline" className="w-full sm:w-auto h-12 px-6 text-sm sm:text-base rounded-full border-[#D5CFC7] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white hover:border-[#1A1A1A]">
                  Läs mer om oss
                </Button>
              </Link>
            </div>

            {/* Mobile mascot illustration (replaces desktop dashboard mockup on small screens) */}
            <div className="lg:hidden flex justify-center mt-10">
              <div className="relative">
                <div className="absolute inset-0 bg-[#E8613A]/15 blur-3xl rounded-full" />
                <img src="/operio-mascot-3d.png" alt="Operio maskot" className="relative w-44 sm:w-56 drop-shadow-2xl" />
              </div>
            </div>

            {/* Value props — honest, no inflated numbers */}
            <div className="grid grid-cols-3 gap-4 sm:gap-8 mt-10 sm:mt-12 pt-6 sm:pt-8 border-t border-[#E8E2D9]">
              {[
                { num: 'AI-driven', label: 'BOKFÖRING' },
                { num: 'GPS', label: 'TIDRAPPORT' },
                { num: 'Gratis', label: '14 DAGAR' },
              ].map((s, i) => (
                <div key={i} className="text-center lg:text-left">
                  <div className="text-base sm:text-xl font-bold text-[#1A1A1A]">{s.num}</div>
                  <div className="text-[9px] sm:text-[10px] font-medium text-[#7A7570] uppercase tracking-wider mt-0.5 sm:mt-0 sm:ml-1.5 sm:inline">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Dashboard mockup */}
          <div className="relative hidden lg:block">
            <div className="bg-white rounded-2xl shadow-2xl shadow-black/8 border border-[#E8E2D9] overflow-hidden">
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-[#E8E2D9] bg-[#FAFAF7]">
                <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
                <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
                <div className="w-3 h-3 rounded-full bg-[#28CA41]" />
                <span className="text-xs text-[#7A7570] ml-3">Operio Dashboard</span>
              </div>
              <div className="flex">
                <div className="w-40 border-r border-[#E8E2D9] py-3 px-3 space-y-1 text-[10px] text-[#6B6560] bg-[#FAFAF7]">
                  {['Dashboard', 'Projekt', 'Personal', 'Tidrapporter', 'Material & AI', 'Fakturor', 'Bokföring', 'Löner', 'Inställningar'].map((item, i) => (
                    <div key={i} className={`px-2 py-1.5 rounded ${i === 0 ? 'bg-[#E8613A]/10 text-[#E8613A] font-medium' : ''}`}>{item}</div>
                  ))}
                </div>
                <div className="flex-1 p-4 space-y-3">
                  <div className="text-xs font-bold text-[#1A1A1A]">Översikt</div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: 'AKTIVA PROJEKT', val: '3' },
                      { label: 'ANSTÄLLDA', val: '5' },
                      { label: 'VÄNTANDE', val: '2' },
                    ].map((s, i) => (
                      <div key={i} className="bg-[#F7F4EF] rounded-lg p-2.5">
                        <p className="text-[8px] text-[#7A7570] uppercase font-medium">{s.label}</p>
                        <p className="text-lg font-bold text-[#1A1A1A] mt-0.5">{s.val}</p>
                      </div>
                    ))}
                  </div>
                  <div className="bg-[#F7F4EF] rounded-lg p-3">
                    <div className="text-[9px] font-bold text-[#1A1A1A] mb-2">Materialplan — AI Genererad</div>
                    <div className="space-y-1">
                      {['Betong C30/37', 'Armering B500B', 'Formskiva 18mm'].map((item, i) => (
                        <div key={i} className="flex justify-between text-[8px] text-[#6B6560]">
                          <span>{item}</span>
                          <span className="font-medium text-[#1A1A1A]">{(2400 + i * 850).toLocaleString('sv-SE')} kr</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Mascot floating */}
            <img src="/operio-mascot-3d.png" alt="Operio maskot" className="absolute -bottom-10 -right-10 w-36 h-36 drop-shadow-2xl animate-bounce" style={{ animationDuration: '3s' }} />
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm font-medium text-[#E8613A] uppercase tracking-widest mb-4">Vårt uppdrag</p>
          <h2 className="text-xl sm:text-2xl font-bold text-[#1A1A1A] leading-relaxed uppercase tracking-wide" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Vi bygger verktygen som låter entreprenörer fokusera på det de gör bäst — bygga. Operio sköter administrationen.
          </h2>
        </div>
      </section>

      {/* Features */}
      <section id="funktioner" className="py-12 sm:py-16 px-5 sm:px-6" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 sm:mb-12">
            <p className="text-xs sm:text-sm font-medium text-[#E8613A] uppercase tracking-widest mb-3">Funktioner</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Byggt för byggbranschen
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="bg-white rounded-xl p-5 sm:p-6 border border-[#E8E2D9] hover:shadow-md hover:border-[#D5CFC7] transition-all duration-200 group" data-testid={`feature-card-${i}`}>
                  <div className="w-9 h-9 bg-[#E8613A]/8 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                    <Icon size={18} className="text-[#E8613A]" />
                  </div>
                  <h3 className="text-sm font-bold text-[#1A1A1A] mb-1.5">{f.title}</h3>
                  <p className="text-xs text-[#6B6560] leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="hur-det-fungerar" className="py-12 sm:py-16 px-5 sm:px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8 sm:mb-12">
            <p className="text-xs sm:text-sm font-medium text-[#E8613A] uppercase tracking-widest mb-3">Process</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Så fungerar det
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {steps.map((step, i) => (
              <div key={i} className="relative" data-testid={`step-${i}`}>
                <span className="text-4xl sm:text-5xl font-bold text-[#E8E2D9]">{step.num}</span>
                <h3 className="text-base font-bold text-[#1A1A1A] mt-2 sm:mt-3">{step.title}</h3>
                <p className="text-sm text-[#6B6560] mt-2 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="priser" className="py-12 sm:py-16 px-5 sm:px-6" data-testid="pricing-section">
        <div className="max-w-5xl mx-auto">
          <div className="mb-10 sm:mb-12 text-center">
            <p className="text-xs sm:text-sm font-medium text-[#E8613A] uppercase tracking-widest mb-3">Priser</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Enkla och transparenta priser
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-4">
            {plans.map((plan, i) => (
              <div key={i} className={`rounded-xl p-6 sm:p-7 transition-all duration-200 ${
                plan.highlighted
                  ? 'bg-[#1A1A1A] text-white ring-1 ring-[#1A1A1A] md:scale-[1.02] shadow-xl'
                  : 'bg-white border border-[#E8E2D9]'
              }`} data-testid={`plan-card-${i}`}>
                {plan.highlighted && <p className="text-[10px] font-bold text-[#E8613A] uppercase tracking-wider mb-3">Mest populär</p>}
                <p className={`text-xs font-bold uppercase tracking-wider ${plan.highlighted ? 'text-white/70' : 'text-[#7A7570]'}`}>{plan.name}</p>
                <div className="flex items-baseline gap-1 mt-2">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  <span className={`text-sm ${plan.highlighted ? 'text-white/60' : 'text-[#7A7570]'}`}> kr{plan.period}</span>
                </div>
                <p className={`text-[10px] font-medium uppercase tracking-wider mt-0.5 ${plan.highlighted ? 'text-white/60' : 'text-[#9B9490]'}`}>exkl. 25 % moms</p>
                <p className={`text-xs mt-1 ${plan.highlighted ? 'text-white/60' : 'text-[#7A7570]'}`}>{plan.perUser}</p>
                <ul className="mt-6 space-y-2.5">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2.5 text-sm">
                      <Check size={14} className="text-[#E8613A]" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register" className="block mt-7">
                  <Button className={`w-full rounded-full h-11 ${
                    plan.highlighted
                      ? 'bg-[#E8613A] hover:bg-[#D4532F] text-white'
                      : 'bg-[#1A1A1A] hover:bg-[#333] text-white'
                  }`}>
                    Kom igång <ChevronRight size={16} className="ml-1" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-[#7A7570] mt-8">Alla priser exkl. 25 % moms. 14 dagars gratis testperiod.</p>
        </div>
      </section>

      {/* Funktionssidor - SEO-länkar */}
      <section className="py-16 sm:py-20 px-5 sm:px-6 bg-[#F2EEE7] border-y border-[#E8E2D9]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Utforska funktion för funktion
            </h2>
            <p className="text-sm text-[#6B6560] mt-3 max-w-xl mx-auto">
              Djup­dykning per område — perfekt om du letar efter ett specifikt verktyg.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { to: '/bygg-kalkyl-program', title: 'Bygg kalkyl­program', desc: 'AI som läser ritningar och skapar offerter på 30 sekunder.', tag: 'Kalkyl' },
              { to: '/tidrapportering-bygg', title: 'Tidrapportering bygg', desc: 'GPS-stämpling, OB-regler och lönexport automatiskt.', tag: 'Personal' },
              { to: '/priser', title: 'Priser & paket', desc: 'Från 1 990 kr/månad exkl. moms. Tre paket, ingen bindning.', tag: 'Priser' },
              { to: '/blogg/basta-bygg-kalkyl-program-2026', title: 'Bästa bygg­program 2026', desc: 'Vi jämför 6 verktyg på svenska marknaden.', tag: 'Guide' },
              { to: '/blogg/digitalisering-byggbranschen-guide', title: 'Digitalisering byggbranschen', desc: '90-dagars­plan från Excel till AI-driven verksamhet.', tag: 'Strategi' },
              { to: '/kontakt', title: 'Boka demo', desc: 'Skicka ett meddelande så hör vi av oss inom 1 arbetsdag.', tag: 'Kontakt' },
            ].map((c) => (
              <Link key={c.to} to={c.to} className="group bg-white rounded-2xl border border-[#E8E2D9] p-5 hover:border-[#E8613A]/40 hover:shadow-md transition-all">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#E8613A]">{c.tag}</p>
                <h3 className="text-base font-bold text-[#1A1A1A] mt-1.5 group-hover:text-[#E8613A] transition-colors">{c.title}</h3>
                <p className="text-xs text-[#6B6560] mt-1 leading-relaxed">{c.desc}</p>
                <span className="inline-flex items-center gap-1 text-xs font-medium text-[#E8613A] mt-3">Läs mer <ArrowRight size={11} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ - SEO + JSON-LD-matchande */}
      <section className="py-16 sm:py-20 px-5 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#1A1A1A] mb-8 text-center" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Vanliga frågor om Operio
          </h2>
          <div className="space-y-3">
            {[
              { q: 'Vad är Operio?', a: 'Operio är en AI-driven SaaS-plattform för svenska bygg-, mark- och entreprenadföretag. Vi kombinerar kalkyl, tidrapportering, fakturering, bokföring och BankID-signering i ett enda system.' },
              { q: 'Hur snabbt kan jag komma igång?', a: '5 minuter. Skapa konto, importera dina priser och du kan räkna din första offert med AI. 14 dagar gratis utan kortuppgifter.' },
              { q: 'Funkar Operio offline?', a: 'Ja, Worker-appen (PWA) fungerar offline för tidrapportering och synkar när nätet kommer tillbaka.' },
              { q: 'Är Operio dyrt?', a: 'Från 1 990 kr/månad exkl. moms. De flesta byggare betalar tillbaka kostnaden inom första veckan tack vare snabbare offerter och korrekt tidrapportering.' },
              { q: 'Kan jag exportera till Fortnox eller Bokio?', a: 'Ja, lönexport och fakturaexport stöds. Direkt Fortnox/Bokio-integration rullas ut Q2 2026.' },
            ].map((f, i) => (
              <details key={i} className="group bg-white border border-[#E8E2D9] rounded-xl px-5 py-4 open:shadow-sm">
                <summary className="cursor-pointer text-base font-bold text-[#1A1A1A] flex justify-between items-center list-none">
                  <span className="pr-4">{f.q}</span>
                  <span className="text-[#E8613A] text-xl flex-shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-sm text-[#6B6560] mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-[#1A1A1A]">
        <div className="max-w-3xl mx-auto text-center">
          <img src="/operio-mascot-3d.png" alt="Operio maskot" className="w-32 h-32 mx-auto mb-4 drop-shadow-2xl" />
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Redo att digitalisera ditt företag?
          </h2>
          <p className="text-white/70 mt-4 max-w-md mx-auto">
            Testa Operio gratis i 14 dagar.
          </p>
          <div className="flex items-center justify-center gap-3 mt-8">
            <Link to="/register">
              <Button className="bg-[#E8613A] hover:bg-[#D4532F] text-white h-12 px-8 text-base rounded-full shadow-lg shadow-[#E8613A]/30">
                Kom igång gratis <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 sm:py-14 px-5 sm:px-6 border-t border-[#E8E2D9] bg-[#F2EEE7]">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-5 gap-8">
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-2">
              <img src="/operio-logo-2.png" alt="Operio" className="h-7 w-auto" />
            </Link>
            <p className="text-sm text-[#6B6560] mt-3 max-w-xs">
              AI-plattformen som digitaliserar bygg-, mark- och entreprenadföretag i Sverige.
            </p>
            <p className="text-xs text-[#7A7570] mt-4">&copy; 2026 Operio AB · Skövde, Sverige</p>
          </div>
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-[#1A1A1A] mb-3">Produkt</h4>
            <ul className="space-y-2 text-sm text-[#6B6560]">
              <li><Link to="/bygg-kalkyl-program" className="hover:text-[#1A1A1A]">Bygg kalkyl­program</Link></li>
              <li><Link to="/tidrapportering-bygg" className="hover:text-[#1A1A1A]">Tidrapportering</Link></li>
              <li><Link to="/priser" className="hover:text-[#1A1A1A]">Priser</Link></li>
              <li><Link to="/register" className="hover:text-[#1A1A1A]">Starta gratis</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-[#1A1A1A] mb-3">Företag</h4>
            <ul className="space-y-2 text-sm text-[#6B6560]">
              <li><Link to="/about" className="hover:text-[#1A1A1A]">Om oss</Link></li>
              <li><Link to="/blogg" className="hover:text-[#1A1A1A]">Blogg</Link></li>
              <li><Link to="/kontakt" className="hover:text-[#1A1A1A]">Kontakt</Link></li>
              <li><a href="mailto:kontakt@operio.nu" className="hover:text-[#1A1A1A]">kontakt@operio.nu</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-[#1A1A1A] mb-3">Logga in</h4>
            <ul className="space-y-2 text-sm text-[#6B6560]">
              <li><Link to="/login" className="hover:text-[#1A1A1A]">Admin</Link></li>
              <li><Link to="/worker/login" className="hover:text-[#1A1A1A]">Anställda</Link></li>
              <li><Link to="/customer/login" className="hover:text-[#1A1A1A]">Kundportal</Link></li>
            </ul>
          </div>
        </div>
      </footer>
      <PublicChatWidget />
    </div>
  );
};

export default LandingPage;
